import { availabilitySellTripSelectionSuccess } from '@actions/availability.action';
import {
  BookingActionTypes,
  bookingGetBookingAfterPassengerUpdateSuccess,
  bookingGetBookingSuccess,
  bookingRetrieveBookingSuccess,
  bookingStoreBooking,
} from '@actions/booking.action';
import {
  clearJourneyMeal,
  mealsChangeSelection,
  mealsSetSameForAllFlights,
  mealsSetSameForAllPax,
  mealsSetSelection,
  selectDefaultPassengersMeals,
  selectPassengerMeal,
  selectSamePassengersMealForReturnFlight,
} from '@actions/meals.action';
import { MealServiceType, MealsSsrEnum } from '@constants/meals.constants';
import { MealService } from '@models/booking.models';
import {
  MealsPassengerSsrsUpdate,
  MealsSsrPassengerUpdateRequest,
  MealsSsrUpdate,
} from '@models/cms/meals.model';

import { createReducer, on, Action } from '@ngrx/store';
import { getPassengerDefaultMealSsr, getPassengerSelectedSsrs, mergeSsrCodes } from '@utils/meals.utils';

export interface State {
  keepSameMealForAllFlights: boolean;
  mealSelections: MealsSsrPassengerUpdateRequest[] | null;
}

export const INITIAL_STATE: State = {
  mealSelections: null,
  keepSameMealForAllFlights: false,
};

const reducer = createReducer(
  INITIAL_STATE,

  on(
    availabilitySellTripSelectionSuccess,
    (state): State => ({
      ...state,
      mealSelections: null,
    }),
  ),

  on(
    mealsSetSelection,
    (state, action): State => ({
      ...state,
      mealSelections: action.payload,
    }),
  ),

  on(
    bookingStoreBooking,
    bookingGetBookingSuccess,
    bookingGetBookingAfterPassengerUpdateSuccess,
    bookingRetrieveBookingSuccess,
    (state, action): State => {
      const booking =
        action.type === BookingActionTypes.storeBooking
          ? action.payload
          : action.booking;

      const getBookingMealsSsrs = (
        firstService: MealService[],
        secondService: MealService[],
      ): MealsSsrUpdate[] | [] => {
        const bothSelected =
          firstService.length > 0 && secondService.length > 0;
        const onlyFirstSelected =
          firstService.length > 0 && secondService.length === 0;
        const onlySecondSelected =
          firstService.length === 0 && secondService.length > 0;

        /* eslint-disable @typescript-eslint/no-non-null-assertion */

        /* istanbul ignore else */
        if (onlyFirstSelected) {
          return [
            {
              ssrCode: firstService[0]!.ssrCode as MealsSsrEnum,
              price: firstService[0]!.totalPrice,
              count: 1,
              included:
                firstService[0]!.inBundleCount === 1 ||
                firstService[0]!.totalPrice === 0,
              paid: firstService[0]!.paidCount === 1,
              serviceType: MealServiceType.FIRST_SERVICE,
            },
          ];
        }
        /* istanbul ignore else */
        if (onlySecondSelected) {
          return [
            {
              ssrCode: secondService[0]!.ssrCode as MealsSsrEnum,
              price: secondService[0]!.totalPrice,
              count: 1,
              included:
                secondService[0]!.inBundleCount === 1 ||
                secondService[0]!.totalPrice === 0,
              paid: secondService[0]!.paidCount === 1,
              serviceType: MealServiceType.SECOND_SERVICE,
            },
          ];
        }
        /* istanbul ignore else */
        if (bothSelected) {
          return [
            {
              ssrCode: firstService[0]!.ssrCode as MealsSsrEnum,
              price: firstService[0]!.totalPrice,
              count: 1,
              included:
                firstService[0]!.inBundleCount === 1 ||
                firstService[0]!.totalPrice === 0,
              paid: firstService[0]!.paidCount === 1,
              serviceType: MealServiceType.FIRST_SERVICE,
            },
            {
              ssrCode: secondService[0]!.ssrCode as MealsSsrEnum,
              price: secondService[0]!.totalPrice,
              count: 1,
              included:
                secondService[0]!.inBundleCount === 1 ||
                secondService[0]!.totalPrice === 0,
              paid: secondService[0]!.paidCount === 1,
              serviceType: MealServiceType.SECOND_SERVICE,
            },
          ];
        }

        /* eslint-enable @typescript-eslint/no-non-null-assertion */

        return [];
      };

      return {
        ...state,
        mealSelections:
          booking.meals?.map((mib) => ({
            legKey: mib.legKey,
            passengers:
              booking.meals
                ?.find((bmi) => bmi.legKey === mib.legKey)
                ?.passengerMeals.map(
                  (pm) =>
                    ({
                      passengerKey: pm.passengerKey,
                      ssrs: getBookingMealsSsrs(
                        pm.firstService,
                        pm.secondService,
                      ),
                    }) as MealsPassengerSsrsUpdate,
                ) ?? [],
          })) ?? [],
      };
    },
  ),

  on(mealsChangeSelection, (state, action): State => {
    return {
      ...state,
      mealSelections:
        state.mealSelections?.map((ms) => {
          if (ms.legKey === action.payload.legKey) {
            const pax: MealsPassengerSsrsUpdate[] = ms.passengers.map((p) => {
              if (p.passengerKey === action.payload.paxKey) {
                if (
                  p.ssrs.length > 0 &&
                  p.ssrs.findIndex(
                    (i) => i.serviceType === action.payload.serviceType,
                  ) !== -1
                ) {
                  if (
                    !action.payload.ssrFirstService &&
                    !action.payload.ssrSecondService
                  ) {
                    // No Meal selection.
                    return {
                      ...p,
                      ssrs: p.ssrs
                        .map((s: MealsSsrUpdate) => ({ ...s }))
                        .filter(
                          (s: MealsSsrUpdate) =>
                            s.serviceType !== action.payload.serviceType,
                        ),
                    };
                  } else {
                    return {
                      ...p,
                      ssrs: p.ssrs.map((s) => {
                        if (s.serviceType === action.payload.serviceType) {
                          return {
                            ...s,
                            ssrCode: (s.serviceType ===
                            MealServiceType.FIRST_SERVICE
                              ? action.payload.ssrFirstService
                              : action.payload
                                  .ssrSecondService) as MealsSsrEnum,
                            price:
                              (s.serviceType === MealServiceType.FIRST_SERVICE
                                ? action.payload.priceFirstService
                                : action.payload.priceSecondService) ?? 0,
                          };
                        } else {
                          return { ...s };
                        }
                      }),
                    };
                  }
                } else if (
                  p.ssrs.length > 0 &&
                  p.ssrs.findIndex(
                    (i) => i.serviceType === action.payload.serviceType,
                  ) === -1
                ) {
                  // New Add
                  return {
                    ...p,
                    ssrs: p.ssrs
                      .map((s) => ({ ...s }))
                      .concat({
                        ssrCode: (action.payload.serviceType ===
                        MealServiceType.FIRST_SERVICE
                          ? action.payload.ssrFirstService
                          : action.payload.ssrSecondService) as MealsSsrEnum,
                        price:
                          (action.payload.serviceType ===
                          MealServiceType.FIRST_SERVICE
                            ? action.payload.priceFirstService
                            : action.payload.priceSecondService) ?? 0,
                        count: 1,
                        included: false,
                        paid: false,
                        serviceType: action.payload.serviceType,
                      }),
                  };
                } else {
                  //Empty Ssr[] so its a new Add
                  return {
                    ...p,
                    ssrs: [
                      {
                        ssrCode: (action.payload.serviceType ===
                        MealServiceType.FIRST_SERVICE
                          ? action.payload.ssrFirstService
                          : action.payload.ssrSecondService) as MealsSsrEnum,
                        price:
                          (action.payload.serviceType ===
                          MealServiceType.FIRST_SERVICE
                            ? action.payload.priceFirstService
                            : action.payload.priceSecondService) ?? 0,
                        count: 1,
                        included: false,
                        paid: false,
                        serviceType: action.payload.serviceType,
                      },
                    ],
                  };
                }
              } else {
                return {
                  ...p,
                  ssrs: p.ssrs.map((s) => ({ ...s })),
                };
              }
            });

            return {
              ...ms,
              passengers: pax,
            };
          } else {
            return {
              ...ms,
              passengers: ms.passengers.map((p) => {
                return {
                  ...p,
                  ssrs: p.ssrs.map((s) => ({ ...s })),
                };
              }),
            };
          }
        }) ?? [],
    };
  }),

  on(mealsSetSameForAllPax, (state, action): State => {
    const includedSegment =
      state.mealSelections
        ?.find((ms) => ms.legKey === action.payload.legKey)
        ?.passengers?.some((p) => p.ssrs.some((s) => s.included)) ?? false;

    /* No meal selection with nothing included */
    /* istanbul ignore else */
    if (!action.payload.ssrCode && !includedSegment) {
      return {
        ...state,
        mealSelections:
          state.mealSelections?.map((ms) => {
            if (ms.legKey === action.payload.legKey) {
              return {
                ...ms,
                passengers: ms.passengers.map((p) => {
                  return {
                    ...p,
                    ssrs:
                      p.ssrs.length > 0
                        ? p.ssrs.filter(
                            (s) => s.serviceType != action.payload.serviceType,
                          )
                        : [],
                  };
                }),
              };
            } else {
              return {
                ...ms,
                passengers: ms.passengers.map((p) => {
                  return {
                    ...p,
                    ssrs: p.ssrs.map((s) => ({ ...s })),
                  };
                }),
              };
            }
          }) ?? [],
      };
    }

    /* Active meal selected with nothing included */
    /* istanbul ignore else */
    if (action.payload.ssrCode && !includedSegment) {
      return {
        ...state,
        mealSelections:
          state.mealSelections?.map((ms) => {
            if (ms.legKey === action.payload.legKey) {
              return {
                ...ms,
                passengers: ms.passengers.map((p) => {
                  return {
                    ...p,
                    ssrs:
                      p.ssrs.length > 0 &&
                      p.ssrs.findIndex(
                        (s) => s.serviceType === action.payload.serviceType,
                      ) !== -1
                        ? p.ssrs.map((s) => {
                            return {
                              ...s,
                              ssrCode: (s.serviceType ===
                              action.payload.serviceType
                                ? action.payload.ssrCode
                                : s.ssrCode) as MealsSsrEnum,
                              price:
                                s.serviceType === action.payload.serviceType
                                  ? action.payload.price
                                  : s.price,
                            };
                          })
                        : p.ssrs.length > 0 &&
                          p.ssrs.findIndex(
                            (s) => s.serviceType === action.payload.serviceType,
                          ) === -1
                        ? p.ssrs
                            .map((s) => ({ ...s }))
                            .concat({
                              ssrCode: action.payload.ssrCode as MealsSsrEnum,
                              serviceType: action.payload.serviceType,
                              price: action.payload.price,
                              included: false,
                              paid: false,
                              count: 1,
                            })
                        : [
                            {
                              ssrCode: action.payload.ssrCode as MealsSsrEnum,
                              serviceType: action.payload.serviceType,
                              price: action.payload.price,
                              included: false,
                              paid: false,
                              count: 1,
                            },
                          ],
                  };
                }),
              };
            } else {
              return {
                ...ms,
                passengers: ms.passengers.map((p) => {
                  return {
                    ...p,
                    ssrs: p.ssrs.map((s) => ({ ...s })),
                  };
                }),
              };
            }
          }) ?? [],
      };
    }

    /* No meal selected with some thing included */
    /* istanbul ignore else */
    if (!action.payload.ssrCode && includedSegment) {
      return {
        ...state,
        mealSelections:
          state.mealSelections?.map((ms) => {
            if (ms.legKey === action.payload.legKey) {
              return {
                ...ms,
                passengers: ms.passengers.map((p) => {
                  return {
                    ...p,
                    ssrs:
                      p.ssrs.length > 0
                        ? p.ssrs.filter(
                            (s) => s.serviceType != action.payload.serviceType,
                          )
                        : [],
                  };
                }),
              };
            } else {
              return {
                ...ms,
                passengers: ms.passengers.map((p) => {
                  return {
                    ...p,
                    ssrs: p.ssrs.map((s) => ({ ...s })),
                  };
                }),
              };
            }
          }) ?? [],
      };
    }

    /* Active meal selected with some thing included */
    /* istanbul ignore else */
    if (action.payload.ssrCode && includedSegment) {
      return {
        ...state,
        mealSelections:
          state.mealSelections?.map((ms) => {
            if (ms.legKey === action.payload.legKey) {
              return {
                ...ms,
                passengers: ms.passengers.map((p) => {
                  return {
                    ...p,
                    ssrs:
                      p.ssrs.findIndex(
                        (s) => s.serviceType === action.payload.serviceType,
                      ) !== -1
                        ? p.ssrs.map((s) => {
                            return {
                              ...s,
                              ssrCode: (s.serviceType ===
                              action.payload.serviceType
                                ? action.payload.ssrCode
                                : s.ssrCode) as MealsSsrEnum,
                            };
                          })
                        : p.ssrs
                            .map((s) => ({ ...s }))
                            .concat({
                              ssrCode: action.payload.ssrCode as MealsSsrEnum,
                              serviceType: action.payload.serviceType,
                              price: action.payload.price,
                              included: false,
                              paid: false,
                              count: 1,
                            }),
                  };
                }),
              };
            } else {
              return {
                ...ms,
                passengers: ms.passengers.map((p) => {
                  return {
                    ...p,
                    ssrs: p.ssrs.map((s) => ({ ...s })),
                  };
                }),
              };
            }
          }) ?? [],
      };
    }
    return state;
  }),

  on(mealsSetSameForAllFlights, (state, action): State => {
    if (!action.payload.isOn) {
      return {
        ...state,
        keepSameMealForAllFlights: false,
      };
    } else {
      const activeSegment = state.mealSelections?.find(
        (ms) => ms.legKey === action.payload.activeLegKey,
      );
      return {
        ...state,
        keepSameMealForAllFlights: true,
        mealSelections:
          state.mealSelections?.map((ms) => {
            /* istanbul ignore else */
            if (
              ms.legKey === action.payload.activeLegKey ||
              !action.payload.legsWithSsrAvailability?.some(
                (s) => s === ms.legKey,
              )
            ) {
              return {
                ...ms,
              };
            }
            return {
              ...ms,
              passengers: ms.passengers.map((p) => {
                const calcSsrs = () => {
                  const activeSegmentSsrs =
                    activeSegment?.passengers.find(
                      (asp) => asp.passengerKey === p.passengerKey,
                    )?.ssrs ?? [];
                  const activeSegmentFirstService = activeSegmentSsrs.find(
                    (s) => s.serviceType === MealServiceType.FIRST_SERVICE,
                  );
                  const activeSegmentSecondService = activeSegmentSsrs.find(
                    (s) => s.serviceType === MealServiceType.SECOND_SERVICE,
                  );
                  const originalFirstService = p.ssrs.find(
                    (s) => s.serviceType === MealServiceType.FIRST_SERVICE,
                  );
                  const originalSecondService = p.ssrs.find(
                    (s) => s.serviceType === MealServiceType.SECOND_SERVICE,
                  );
                  const mealsSsrUpdate: MealsSsrUpdate[] = [];
                  /* istanbul ignore else */
                  if (p.ssrs.length === 0) {
                    activeSegmentFirstService &&
                      mealsSsrUpdate.push({
                        ...activeSegmentFirstService,
                        price:
                          action.payload.priceFirstService ||
                          (action.payload.priceOriginalSelectedFirstService ??
                            0),
                        included: false,
                        paid: false,
                      });
                    activeSegmentSecondService &&
                      mealsSsrUpdate.push({
                        ...activeSegmentSecondService,
                        price:
                          action.payload.priceSecondService ||
                          (action.payload.priceOriginalSelectedSecondService ??
                            0),
                        included: false,
                        paid: false,
                      });
                  }
                  /* istanbul ignore else */
                  if (p.ssrs.length > 0) {
                    if (activeSegmentFirstService) {
                      mealsSsrUpdate.push(
                        originalFirstService
                          ? {
                              ...originalFirstService,
                              ssrCode: activeSegmentFirstService.ssrCode,
                            }
                          : {
                              ...activeSegmentFirstService,
                              price:
                                action.payload.priceFirstService ||
                                action.payload
                                  .priceOriginalSelectedFirstService ||
                                0,

                              included: false,
                              paid: false,
                            },
                      );
                    } else {
                      originalFirstService &&
                        mealsSsrUpdate.push({ ...originalFirstService });
                    }

                    /* istanbul ignore else */
                    if (activeSegmentSecondService) {
                      mealsSsrUpdate.push(
                        originalSecondService
                          ? {
                              ...originalSecondService,
                              ssrCode: activeSegmentSecondService.ssrCode,
                            }
                          : {
                              ...activeSegmentSecondService,
                              price:
                                action.payload.priceSecondService ||
                                action.payload
                                  .priceOriginalSelectedSecondService ||
                                0,
                              included: false,
                              paid: false,
                            },
                      );
                    } else {
                      originalSecondService &&
                        mealsSsrUpdate.push({ ...originalSecondService });
                    }
                  }
                  return mealsSsrUpdate;
                };
                return {
                  ...p,
                  ssrs: calcSsrs(),
                };
              }),
            };
          }) ?? [],
      };
    }
  }),

  on(selectPassengerMeal, (state, action): State => {
    const { payload } = action;

    return {
      ...state,
      mealSelections: (state.mealSelections || []).map(m => {
        if (payload.legKey && m.legKey !== payload.legKey) {
          return m;
        }

        return {
          ...m,
          passengers: m.passengers.map(p => {
            if (p.passengerKey !== payload.mealPassenger.key) {
              return p;
            }

            const updatedSsrs = getPassengerSelectedSsrs(p.ssrs, payload);
            return {
              ...p,
              ssrs: updatedSsrs
            };
          })
        };
      })
    };
  }),
  on(selectSamePassengersMealForReturnFlight, (state, action): State => {
    const { serviceType, legs } = action.payload;
    const { mealSelections } = state;

    if (!mealSelections) {
      return state;
    }
    const sourceLeg = legs[0];

    const sourceFlight = mealSelections.find(m => m.legKey === sourceLeg?.legKey);
    if (!sourceFlight) {
      return state;
    }

    const otherFlights = mealSelections.filter(f => f.legKey !== sourceLeg?.legKey);
    const updatedOtherFlights = otherFlights.map(o => {
      return {
        ...o,
        passengers: o.passengers.map(p => {
          const sourcePassenger = sourceFlight.passengers.find(px => px.passengerKey === p.passengerKey);
          if (!sourcePassenger) {
            return { ...p };
          }

          const sourceSsr = sourcePassenger.ssrs.find(sx => sx.serviceType === serviceType);
          if (!sourceSsr) {
            return {
              ...p,
              ssrs: p.ssrs.filter(s => s.serviceType != serviceType)
             };
          }

          const ssrIndex = p.ssrs.findIndex(s => s.serviceType === serviceType);
          if (ssrIndex !== -1) {
            return {
              ...p,
              ssrs: p.ssrs.map((s, index) =>
                index === ssrIndex ? { ...s, ssrCode: sourceSsr.ssrCode } : s
              )
            };
          } else {
            const passengerOptions = legs.find(l => l.legKey === o.legKey)?.passengers.find(lp => lp.key === sourcePassenger.passengerKey)?.options;
            const newSsr = {
              serviceType,
              ssrCode: sourceSsr.ssrCode,
              count: 1,
              included: false,
              paid: false,
              price: (passengerOptions?.find(po => po.ssrCode === sourceSsr.ssrCode) || { price: 0 }).price
            };
            return {
              ...p,
              ssrs: [...p.ssrs, newSsr]
            };
          }
        })
      };
    });

    const updatedMealSelections = [sourceFlight, ...updatedOtherFlights];
    return {
      ...state,
      mealSelections: updatedMealSelections
    };
  }),
  on(clearJourneyMeal, (state, action): State => {
    return {
      ...state,
      mealSelections: (state.mealSelections || []).map(m => {
        return {
          ...m,
          passengers: m.passengers.map(p => {
            return {
              ...p,
              ssrs: [...p.ssrs.filter(s => s.serviceType !== action.serviceType || s.paid || s.included)]
            };
          })
        };
      })
    };
  }),
  on(selectDefaultPassengersMeals, (state, action): State => {
    return {
      ...state,
      mealSelections: (state.mealSelections || []).map(m => {
        return {
          ...m,
          passengers: m.passengers.map(p => {
            const defaultSsrCode = getPassengerDefaultMealSsr(action.addPayload.serviceType);
            const defaultSsr = {
              count: 1,
              included: false,
              paid: false,
              price: action.addPayload.price,
              serviceType: action.addPayload.serviceType,
              ssrCode: defaultSsrCode
            }
            const mergedSrrs = mergeSsrCodes(p.ssrs, defaultSsr);
            return {
              ...p,
              ssrs: mergedSrrs
            };
          })
        };
      })
    };
  })
);

export const mealsReducer = (state: State, action: Action): State =>
  reducer(state, action);
